// frontend/src/components/tickets/TicketForm.js
// The "Ticket details / + Add JIRA ticket" form from the spec.
// Fields: Task (TEXTxxxxxxxx), Task Type, Type of activity, Customer,
// Engineer, Priority, Status, Comments, Attachments.
// Tickets skip QC entirely (iteration 2 review) -- no QC Engineer,
// Description or CO Number field; see backend/src/utils/statusFlow.js
// TICKET_MAIN_FLOW for the resulting simplified status flow.
import React, { useEffect, useState } from 'react';
import {
    Box, Grid, TextField, Button, MenuItem, Typography, Divider, Alert,
    CircularProgress, List, ListItem, ListItemText, IconButton,
} from '@mui/material';
import { Save, Cancel, CloudUpload, Delete, AttachFile } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useAuth } from '../../context/AuthContext';
import useReferenceData from '../../hooks/useReferenceData';
import { createTicket, updateTicket, uploadTicketFiles } from '../../services/endpoints';
import { isManagerRole, fmtHours } from '../../constants/domain';
import { parseHours } from '../parts/PartForm';
import HoursField, { isValidHhMm } from '../common/HoursField';
import RichCommentEditor from '../common/RichCommentEditor';
import { isBlankHtml } from '../../utils/richText';

const EMPTY = {
    ticket_key: '',
    task_type: 'PSG',
    type_of_activity: 'Product development',
    customer: '',
    assigned_to: '',
    priority: 'Medium',
    status: 'Yet to Start',
    start_date: '',
    due_date: '',
    estimated_hours: '',
    actual_hours: '',
    comments: '',
};

const TicketForm = ({ ticket, onSuccess, onCancel }) => {
    const { user } = useAuth();
    const isEdit = !!ticket?.id;
    const manager = isManagerRole(user?.role);
    const ref = useReferenceData();

    const [form, setForm] = useState(EMPTY);
    // Actual hours accrue automatically once work starts (see
    // Ticket.changeStatus) -- there's nothing to show or log before the
    // ticket has left "Yet to Start", so the field only appears from In
    // Progress on.
    const hideActualHours = form.status === 'Yet to Start';
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState([]);

    useEffect(() => {
        if (!ticket) { setForm(EMPTY); return; }
        setForm({
            ...EMPTY,
            ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, ticket[k] ?? ''])),
            // Hours are always entered/shown as HH:MM; storage stays decimal.
            estimated_hours: fmtHours(ticket.estimated_hours) || '',
            actual_hours: fmtHours(ticket.actual_hours) || '',
            comments: '',
        });
    }, [ticket]);

    const isCompleted = ticket?.status === 'Completed';

    const set = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.ticket_key.trim()) e.ticket_key = 'Ticket / task id is required';
        if (!form.task_type) e.task_type = 'Task type is required';
        if (!isValidHhMm(form.estimated_hours)) {
            e.estimated_hours = 'Use HH:MM, e.g. 08:30';
        }
        if (!isValidHhMm(form.actual_hours)) {
            e.actual_hours = 'Use HH:MM, e.g. 08:30';
        }
        if (form.start_date && form.due_date && form.due_date < form.start_date) {
            e.due_date = 'Target date cannot be before the start date';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) {
            toast.error('Please fix the highlighted fields');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ticket_key: form.ticket_key.trim(),
                task_type: form.task_type,
                type_of_activity: form.type_of_activity || null,
                customer: form.customer.trim() || null,
                priority: form.priority,
                start_date: form.start_date || null,
                due_date: form.due_date || null,
                estimated_hours: parseHours(form.estimated_hours),
            };
            // Actual hours come from the linked parts automatically -- never
            // written back from the (read-only) field.
            if (!ticket?.actual_hours_from_parts) {
                payload.actual_hours = parseHours(form.actual_hours);
            }
            // Anyone can pick the engineer while creating a ticket (matches
            // Add Part); reassigning an existing ticket stays manager-only.
            if (!isEdit || manager) {
                payload.assigned_to = form.assigned_to || null;
            }
            if (!isEdit) {
                payload.status = form.status;
                if (!isBlankHtml(form.comments)) payload.comments = form.comments;
            }

            const res = isEdit
                ? await updateTicket(ticket.id, payload)
                : await createTicket(payload);
            const saved = res.data;

            if (files.length > 0) {
                try {
                    await uploadTicketFiles(saved.id, files);
                } catch (err) {
                    toast.warning(`Ticket saved, but attachments failed: ${err.response?.data?.message || err.message}`);
                }
            }

            toast.success(isEdit
                ? `Ticket ${saved.ticket_key} updated`
                : `Ticket ${saved.ticket_key} created`);
            onSuccess?.(saved);
        } catch (err) {
            const data = err.response?.data;
            if (data?.errors) {
                const mapped = {};
                data.errors.forEach(e2 => { mapped[e2.path || e2.param] = e2.msg; });
                setErrors(mapped);
                toast.error('Please fix the highlighted fields');
            } else {
                toast.error(data?.message || err.message || 'Could not save the ticket');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Task"
                        value={form.ticket_key} onChange={set('ticket_key')}
                        error={!!errors.ticket_key}
                        helperText={errors.ticket_key || 'e.g. TEXT10024581'}
                        disabled={isEdit}
                        placeholder="TEXTxxxxxxxx" />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField select fullWidth required label="Task Type"
                        value={form.task_type} onChange={set('task_type')}
                        error={!!errors.task_type} helperText={errors.task_type}>
                        {ref.taskTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField select fullWidth label="Priority"
                        value={form.priority} onChange={set('priority')}>
                        {ref.priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField select fullWidth label="Type of activity"
                        value={form.type_of_activity} onChange={set('type_of_activity')}>
                        <MenuItem value=""><em>Not specified</em></MenuItem>
                        {ref.activityTypes.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Customer"
                        value={form.customer} onChange={set('customer')} />
                </Grid>

                <Grid item xs={12}>
                    {/* Anyone can pick the engineer while creating a ticket
                        (matches Add Part); reassigning an existing ticket
                        stays manager-only. */}
                    <TextField select fullWidth label="Engineer"
                        value={form.assigned_to} onChange={set('assigned_to')}
                        disabled={isEdit && !manager}
                        helperText={isEdit && !manager ? 'Only managers can reassign work' : ' '}>
                        <MenuItem value=""><em>Unassigned</em></MenuItem>
                        {ref.engineers.map(u => (
                            <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="date" label="Start date"
                        value={form.start_date ? String(form.start_date).slice(0, 10) : ''}
                        onChange={set('start_date')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="date" label="Target date"
                        value={form.due_date ? String(form.due_date).slice(0, 10) : ''}
                        onChange={set('due_date')}
                        error={!!errors.due_date} helperText={errors.due_date}
                        InputLabelProps={{ shrink: true }} />
                </Grid>
                {!isCompleted && (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <HoursField label="Estimated Hours" value={form.estimated_hours}
                                onChange={(v) => setForm(prev => ({ ...prev, estimated_hours: v }))}
                                error={!!errors.estimated_hours} helperText={errors.estimated_hours} />
                        </Grid>
                        {/* Removed from the form while the ticket is still "Yet to
                            Start" -- no work, no hours yet. It appears once the
                            status flow reaches "In Progress" (and stays visible
                            from there on), since actual hours accrue automatically
                            as the ticket moves through status changes (the feed-in
                            flow in Ticket.changeStatus). */}
                        {!hideActualHours && (
                            <Grid item xs={12} sm={6} md={3}>
                                <HoursField label="Actual Hours" value={form.actual_hours}
                                    onChange={(v) => setForm(prev => ({ ...prev, actual_hours: v }))}
                                    disabled={!!ticket?.actual_hours_from_parts}
                                    error={!!errors.actual_hours}
                                    helperText={ticket?.actual_hours_from_parts
                                        ? 'Auto: total of all linked parts'
                                        : errors.actual_hours} />
                            </Grid>
                        )}
                    </>
                )}

                {!isEdit && (
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth label="Status"
                            value={form.status} onChange={set('status')}>
                            {ref.ticketStatuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                    </Grid>
                )}

                {!isEdit && (
                    <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>Comments</Typography>
                        <RichCommentEditor
                            value={form.comments}
                            onChange={(html) => setForm(f => ({ ...f, comments: html }))} />
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="subtitle2" gutterBottom>Attachments</Typography>
                    <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                        Choose files
                        <input hidden multiple type="file"
                            onChange={(e) => setFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                    </Button>
                    {files.length > 0 && (
                        <List dense>
                            {files.map((f, i) => (
                                <ListItem key={`${f.name}-${i}`} disableGutters
                                    secondaryAction={
                                        <IconButton size="small"
                                            onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    }>
                                    <AttachFile fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <ListItemText primary={f.name}
                                        secondary={`${(f.size / 1024 / 1024).toFixed(2)} MB`} />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Grid>
            </Grid>

            {isEdit && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Status moves through the status flow so transitions stay validated -
                    use the status action in the ticket list.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={onCancel} startIcon={<Cancel />} disabled={saving}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
                    {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create ticket'}
                </Button>
            </Box>
        </Box>
    );
};

export default TicketForm;
